"use client";

import { Order, Location as LocationRecord, Tenant, isCheckoutHeldOrderState } from "@order-up/models";
import { Cart } from "@order-up/models";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { CollectionPaths, tenantLocationsPath } from "@order-up/common";
import { FirestoreRepository } from "../interfaces/FirestoreRepository";
import { resolveDocIds } from "../services/resolveDocIds";
import { orderService } from "../services/orders";

/**
 * Defines the query keys used for order-related data.
 */
const orderKeys = {
  all: ["orders"] as const,
  lists: () => [...orderKeys.all, "list"] as const,
  listByCustomer: (customerId: string) =>
    [...orderKeys.lists(), { customerId }] as const,
  details: () => [...orderKeys.all, "detail"] as const,
  detailById: (orderId: number) => [...orderKeys.details(), orderId] as const,
  analytics: () => [...orderKeys.all, "analytics"] as const,
  analyticsForRestaurant: (
    locationNumericId: number,
    tenantId: number,
    startDate: string,
    endDate: string,
  ) =>
    [
      ...orderKeys.analytics(),
      { restaurantId: locationNumericId, tenantId, startDate, endDate },
    ] as const,
};

/**
 * A hook to fetch orders for a specific customer.
 * @param customerId The customer ID.
 * @param tenantDocId The tenant Firestore document ID.
 * @param locationDocId The restaurant Firestore document ID.
 * @param limit Optional limit on results.
 */
const useGetCustomerOrders = (
  customerId: string,
  tenantDocId: string,
  locationDocId: string,
  limit: number = 20,
) => {
  return useQuery({
    queryKey: orderKeys.listByCustomer(customerId),
    queryFn: () =>
      orderService.getCustomerOrders(
        customerId,
        tenantDocId,
        locationDocId,
        limit,
      ),
    enabled: !!customerId && !!tenantDocId && !!locationDocId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * A hook to fetch a single order by its ID.
 * @param orderId The order ID.
 * @param tenantDocId The tenant Firestore document ID.
 * @param locationDocId The restaurant Firestore document ID.
 */
const useGetOrderById = (
  orderId: number,
  tenantDocId: string,
  locationDocId: string,
) => {
  return useQuery({
    queryKey: orderKeys.detailById(orderId),
    queryFn: () =>
      orderService.getOrderById(orderId, tenantDocId, locationDocId),
    enabled: !!orderId && !!tenantDocId && !!locationDocId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * A hook to fetch a single order by its GUID (for QR code checkout).
 *
 * SOC 2 (C9): now resolves via the authenticated single-record API. Scope is
 * the numeric tenant/restaurant business IDs (NOT Firestore doc IDs).
 *
 * @param orderGuid The order GUID.
 * @param tenantId The tenant numeric business ID.
 * @param locationNumericId The restaurant numeric business ID.
 */
const useGetOrderByGuid = (
  orderGuid: string | null,
  tenantId: number,
  locationNumericId: number,
  // Optional polling — the customer order-tracking page passes a refetchInterval
  // so the order's live status/courier progress updates without a manual refresh
  // (post-C9 the order is fetched via the authenticated API, not a Firestore
  // listener, so polling is how we get real-time-ish updates). A function form
  // can read the latest order and stop polling once it reaches a terminal state.
  options?: {
    refetchInterval?:
      | number
      | false
      | ((query: { state: { data?: unknown } }) => number | false);
    // Durable order-access token (checkout response / receipt link) — authorizes
    // the read across devices / after the anon session is gone.
    accessToken?: string;
  },
) => {
  return useQuery({
    queryKey: orderGuid
      ? [...orderKeys.details(), { guid: orderGuid }]
      : ["orders", "null"],
    queryFn: async () => {
      if (!orderGuid) return null;
      return orderService.getOrderByGuid(
        orderGuid,
        tenantId,
        locationNumericId,
        options?.accessToken,
      );
    },
    enabled: !!orderGuid && !!tenantId && !!locationNumericId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchInterval: options?.refetchInterval as never,
  });
};

/**
 * A hook to get an order by cart ID (prevents duplicate orders on payment retry).
 *
 * SOC 2 (C9): now resolves via the authenticated single-record API. Scope is
 * the numeric tenant/restaurant business IDs (NOT Firestore doc IDs).
 *
 * @param cartId The cart ID (numeric)
 * @param tenantId The tenant numeric business ID
 * @param locationNumericId The restaurant numeric business ID
 * @returns A query object from TanStack Query.
 */
const useGetOrderByCartId = (
  cartId: number | null,
  tenantId: number,
  locationNumericId: number,
) => {
  return useQuery({
    queryKey: cartId
      ? [...orderKeys.details(), { cartId }]
      : ["orders", "null"],
    queryFn: async () => {
      if (!cartId) return null;
      return orderService.getOrderByCartId(
        cartId,
        tenantId,
        locationNumericId,
      );
    },
    enabled: !!cartId && !!tenantId && !!locationNumericId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * A hook to update an order's status.
 * @returns A mutation object from TanStack Query.
 */
const useUpdateOrderStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      status,
      tenantDocId,
      restaurantDocId: locationDocId,
      additionalData,
    }: {
      orderId: number;
      status: string;
      tenantDocId: string;
      restaurantDocId: string;
      additionalData?: any;
    }) =>
      orderService.updateOrderStatus(
        orderId,
        status,
        tenantDocId,
        locationDocId,
        additionalData,
      ),
    onSuccess: (_, variables) => {
      // After updating an order, invalidate related queries
      queryClient.invalidateQueries({
        queryKey: orderKeys.detailById(variables.orderId),
      });
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
    },
  });
};

/**
 * A hook to fetch order analytics for a restaurant.
 * @param locationNumericId The restaurant ID.
 * @param tenantId The tenant ID.
 * @param tenantDocId The tenant Firestore document ID.
 * @param locationDocId The restaurant Firestore document ID.
 * @param startDate Start date for analytics.
 * @param endDate End date for analytics.
 */
const useGetOrderAnalytics = (
  locationNumericId: number,
  tenantId: number,
  tenantDocId: string,
  locationDocId: string,
  startDate: Date,
  endDate: Date,
) => {
  const startDateStr = startDate.toISOString();
  const endDateStr = endDate.toISOString();

  return useQuery({
    queryKey: orderKeys.analyticsForRestaurant(
      locationNumericId,
      tenantId,
      startDateStr,
      endDateStr,
    ),
    queryFn: () =>
      orderService.getOrderAnalytics(
        locationNumericId,
        tenantId,
        tenantDocId,
        locationDocId,
        startDate,
        endDate,
      ),
    enabled: !!locationNumericId && !!tenantId && !!tenantDocId && !!locationDocId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * A hook to fetch all orders for a restaurant with optional filtering.
 * @param tenantId The tenant numeric ID.
 * @param locationNumericId The restaurant numeric ID.
 * @param filters Optional filters for order queries.
 * @param limit Optional limit on results.
 */
const useGetLocationOrders = (
  tenantId: number,
  locationNumericId: number,
  filters?: {
    orderSource?: "pos" | "online" | "thirdParty" | "kiosk" | "phone" | "api";
    externalSource?: "ubereats" | "doordash" | "grubhub" | "square" | "toast";
    orderState?:
      | "draft"
      | "pendingAcceptance"
      | "scheduled"
      | "open"
      | "held"
      | "closed"
      | "cancelled"
      | "voided";
    paymentStatus?: "unpaid" | "processing" | "partiallyPaid" | "paid" | "partiallyRefunded" | "refunded" | "voided" | "failed" | "comped";
    startDate?: Date;
    endDate?: Date;
  },
  limit: number = 100,
) => {
  return useQuery({
    queryKey: [
      ...orderKeys.lists(),
      "restaurant",
      { tenantId, restaurantId: locationNumericId, filters, limit },
    ],
    queryFn: () =>
      orderService.getRestaurantOrders(tenantId, locationNumericId, filters, limit),
    enabled: !!tenantId && !!locationNumericId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * As [useGetRestaurantOrders], but keeps the truncation flag.
 *
 * Use this wherever the list is presented as the complete set — the Orders page
 * is headed "All orders from every source", which stops being true the moment
 * the cap bites, and a silent cap is indistinguishable from a full result.
 */
const useGetLocationOrdersPage = (
  tenantId: number,
  locationNumericId: number,
  filters?: Parameters<typeof orderService.getRestaurantOrdersPage>[2],
  limit: number = 100,
) => {
  return useQuery({
    queryKey: [
      ...orderKeys.lists(),
      "restaurant",
      "page",
      { tenantId, restaurantId: locationNumericId, filters, limit },
    ],
    queryFn: () =>
      orderService.getRestaurantOrdersPage(
        tenantId,
        locationNumericId,
        filters,
        limit,
      ),
    enabled: !!tenantId && !!locationNumericId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * A hook to fetch orders from a specific external source (Uber Eats, DoorDash, etc.).
 * @param tenantId The tenant numeric ID.
 * @param locationNumericId The restaurant numeric ID.
 * @param externalSource The external source platform.
 * @param limit Optional limit on results.
 */
const useGetExternalOrders = (
  tenantId: number,
  locationNumericId: number,
  externalSource: "ubereats" | "doordash" | "grubhub" | "square" | "toast",
  limit: number = 100,
) => {
  return useQuery({
    queryKey: [
      ...orderKeys.lists(),
      "external",
      { tenantId, restaurantId: locationNumericId, externalSource, limit },
    ],
    queryFn: () =>
      orderService.getExternalOrders(
        tenantId,
        locationNumericId,
        externalSource,
        limit,
      ),
    enabled: !!tenantId && !!locationNumericId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * A hook to fetch all third-party orders (from any external platform).
 * @param tenantId The tenant numeric ID.
 * @param locationNumericId The restaurant numeric ID.
 * @param limit Optional limit on results.
 */
const useGetThirdPartyOrders = (
  tenantId: number,
  locationNumericId: number,
  limit: number = 100,
) => {
  return useQuery({
    queryKey: [
      ...orderKeys.lists(),
      "thirdParty",
      { tenantId, restaurantId: locationNumericId, limit },
    ],
    queryFn: () =>
      orderService.getThirdPartyOrders(tenantId, locationNumericId, limit),
    enabled: !!tenantId && !!locationNumericId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * A hook to fetch platform orders (POS, online ordering, kiosk).
 * @param tenantId The tenant numeric ID.
 * @param locationNumericId The restaurant numeric ID.
 * @param source The platform source (pos, online, kiosk, phone, api).
 * @param limit Optional limit on results.
 */
const useGetPlatformOrders = (
  tenantId: number,
  locationNumericId: number,
  source: "pos" | "online" | "kiosk" | "phone" | "api",
  limit: number = 100,
) => {
  return useQuery({
    queryKey: [
      ...orderKeys.lists(),
      "platform",
      { tenantId, restaurantId: locationNumericId, source, limit },
    ],
    queryFn: () =>
      orderService.getPlatformOrders(tenantId, locationNumericId, source, limit),
    enabled: !!tenantId && !!locationNumericId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

// ─────────────────────────────────────────────────────────────────────────
// Real-time active-orders subscription
//
// Subscribes to the restaurant's orders subcollection via Firestore onSnapshot
// (FirestoreRepository.subscribeToCollection) and writes snapshots into the
// TanStack Query cache under `orderKeys.lists()` so the Active Deliveries
// board reads them reactively.
//
// A 30-second poll fallback is activated when the listener errors (e.g.
// offline or quota exceeded) and is cleared when the listener recovers on
// next mount.
//
// Three onSnapshot listeners run in parallel, each owning one orderSource
// bucket (so they never clobber each other on merge):
//   1. `orderSource == 'thirdParty'`  — marketplace orders (Uber/DoorDash/GH)
//   2. `orderSource == 'pos'`         — in-store orders with a Drive courier leg
//   3. `orderSource == 'online'`      — first-party web/app orders with a leg
// Listeners 2 & 3 keep only courier-bearing orders (externalDeliveryId set).
// Active/inactive filtering is done client-side by the board to avoid
// disjunctive Firestore queries that would require a composite index.
//
// Tenant and restaurant are resolved once at mount (numeric id → docId lookup
// mirrors the pattern in getRestaurantOrders). Re-resolution is skipped while
// the subscription is alive.
// ─────────────────────────────────────────────────────────────────────────

const ACTIVE_ORDERS_SUBSCRIPTION_LIMIT = 100;
const POLL_FALLBACK_INTERVAL_MS = 30_000;

/** Query-key used to store the live order list in the TanStack cache. */
function activeOrdersKey(tenantId: number, locationNumericId: number) {
  return [...orderKeys.lists(), "active-subscription", { tenantId, restaurantId: locationNumericId }] as const;
}

/**
 * Subscribe to the restaurant's orders collection in real time via Firestore
 * onSnapshot.
 *
 * Returns `{orders, isLoading, error, isLive, lastUpdatedAt, refresh}`.
 *
 * - `orders` — current snapshot, typed as `Order[]`.
 * - `isLoading` — true until the first snapshot arrives.
 * - `error` — set if both the listener AND the poll fallback fail.
 * - `isLive` — true when the onSnapshot listener is active; false when
 *   the 30s poll fallback is in use.
 * - `lastUpdatedAt` — `Date` of the most recent snapshot/poll (or null), for a
 *   staleness indicator during the poll-fallback window.
 * - `refresh` — manual one-shot re-fetch (same path as the poll fallback).
 *
 * **Query-cache sharing:** snapshots are written to `orderKeys.lists()` so
 * the board can also read them via `useQuery({ queryKey: activeOrdersKey(...) })`.
 * Consumers that only need `orders` should use this hook directly instead.
 */
const useActiveOrdersSubscription = (tenantId: number, locationNumericId: number) => {
  const queryClient = useQueryClient();
  const cacheKey = activeOrdersKey(tenantId, locationNumericId);

  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isLive, setIsLive] = useState(false);
  // Timestamp of the most recent snapshot/poll — surfaced so the board can
  // show a "updated Ns ago" staleness indicator during the poll-fallback window.
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  // Refs to hold the unsubscribe function and the poll fallback timer so
  // cleanup in the effect teardown is reliable.
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stable poll callback: fetches third-party (marketplace) orders plus the
  // first-party Drive-dispatched orders from BOTH the 'pos' and 'online'
  // channels (filtered to those carrying an externalDeliveryId). Used as the
  // fallback when the live listener errors AND as the `refresh()` affordance.
  const runPoll = useCallback(async () => {
    if (!tenantId || !locationNumericId) return;
    try {
      const [thirdParty, posOrders, onlineOrders] = await Promise.all([
        orderService.getThirdPartyOrders(tenantId, locationNumericId, ACTIVE_ORDERS_SUBSCRIPTION_LIMIT),
        orderService.getPlatformOrders(tenantId, locationNumericId, "pos", ACTIVE_ORDERS_SUBSCRIPTION_LIMIT),
        orderService.getPlatformOrders(tenantId, locationNumericId, "online", ACTIVE_ORDERS_SUBSCRIPTION_LIMIT),
      ]);
      // First-party channels carry every POS/online order; keep only the ones
      // with a courier leg so the board isn't flooded with dine-in/pickup.
      const firstPartyCourier = [...posOrders, ...onlineOrders].filter(
        (o) => !!(o as Order & { externalDeliveryId?: string }).externalDeliveryId,
      );
      const combined = [...thirdParty, ...firstPartyCourier];
      setOrders(combined);
      queryClient.setQueryData(cacheKey, combined);
      setLastUpdatedAt(new Date());
      setError(null);
      setIsLoading(false);
    } catch (pollErr) {
      setError(pollErr instanceof Error ? pollErr : new Error(String(pollErr)));
      setIsLoading(false);
    }
  }, [tenantId, locationNumericId, queryClient]);

  useEffect(() => {
    if (!tenantId || !locationNumericId) return;

    let mounted = true;

    const startPollFallback = () => {
      if (pollTimerRef.current) return; // already polling
      setIsLive(false);
      void runPoll();
      pollTimerRef.current = setInterval(() => void runPoll(), POLL_FALLBACK_INTERVAL_MS);
    };

    const stopPollFallback = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    // Resolve tenant docId + restaurant docId asynchronously, then attach
    // the listener. This mirrors the resolution in getRestaurantOrders.
    const attachListener = async () => {
      let tenantDocId: string;
      let locationDocId: string;

      try {
        const tenantRepo = new FirestoreRepository<Tenant>(
          CollectionPaths.tenants(),
          Tenant,
        );
        const tenants = await tenantRepo.find(
          [{ field: "tenantId", operator: "==", value: tenantId }],
          [],
          1,
        );
        const tenant = tenants[0];
        if (!tenant) throw new Error(`Tenant not found with ID: ${tenantId}`);
        tenantDocId = tenant.docId;

        const locationRepo = new FirestoreRepository<LocationRecord>(
          tenantLocationsPath(tenantDocId),
          LocationRecord,
        );
        const locations = await locationRepo.find(
          [{ field: "restaurantId", operator: "==", value: locationNumericId }],
          [],
          1,
        );
        const locationRecord = locations[0];
        if (!locationRecord) throw new Error(`Restaurant not found with ID: ${locationNumericId}`);
        locationDocId = locationRecord.docId;
      } catch (resolveErr) {
        if (!mounted) return;
        // Resolution failed — fall back to polling immediately.
        setError(resolveErr instanceof Error ? resolveErr : new Error(String(resolveErr)));
        startPollFallback();
        return;
      }

      const collectionPath = CollectionPaths.orders(tenantDocId, locationDocId);

      // Replace the slice of `orders` owned by one orderSource with a fresh set
      // of docs, write through to the query cache, and stamp lastUpdatedAt. Each
      // listener owns exactly one orderSource bucket, so they never clobber each
      // other (the merge is keyed on orderSource).
      const applySlice = (sourceValue: string, docs: Order[]) => {
        setOrders((prev) => {
          const others = prev.filter(
            (o) => (o as Order).orderSource !== sourceValue,
          );
          const merged = [...others, ...docs.slice(0, ACTIVE_ORDERS_SUBSCRIPTION_LIMIT)];
          queryClient.setQueryData(cacheKey, merged);
          return merged;
        });
        setLastUpdatedAt(new Date());
      };

      // Subscribe to third-party orders (marketplace: Uber Eats, DoorDash, Grubhub).
      const thirdPartyRepo = new FirestoreRepository<Order>(collectionPath, Order);
      const unsubThirdParty = thirdPartyRepo.subscribeToCollection(
        [{ field: "orderSource", operator: "==", value: "thirdParty" }],
        [{ field: "createdAt", direction: "desc" }],
        (docs) => {
          if (!mounted) return;
          applySlice("thirdParty", docs);
          setIsLive(true);
          setIsLoading(false);
          setError(null);
          stopPollFallback();
        },
        (listenerErr) => {
          if (!mounted) return;
          setIsLive(false);
          setError(listenerErr);
          startPollFallback();
        },
        // Bound the live watch to the newest N (already ordered by createdAt
        // desc, served by the (orderSource, createdAt) composite index) so the
        // listener no longer streams + re-materializes every third-party order
        // ever written on each snapshot.
        ACTIVE_ORDERS_SUBSCRIPTION_LIMIT,
      );

      // Subscribe to first-party delivery orders dispatched via a Drive courier.
      // These live under orderSource 'pos' (rung up in-store) OR 'online'
      // (first-party web/app ordering) — BOTH must be covered or self-delivery
      // and online Drive orders are silently absent from the board. Each is a
      // single-equality query (orderSource == X) so the existing
      // (orderSource, createdAt) composite index serves it; we filter to
      // courier-bearing orders client-side to avoid a disjunctive query.
      const subscribeFirstPartyCourier = (sourceValue: "pos" | "online") => {
        const repo = new FirestoreRepository<Order>(collectionPath, Order);
        return repo.subscribeToCollection(
          [{ field: "orderSource", operator: "==", value: sourceValue }],
          [{ field: "createdAt", direction: "desc" }],
          (docs) => {
            if (!mounted) return;
            // Keep only Drive-dispatched orders so the board isn't flooded with
            // every dine-in/pickup order on these channels.
            const courierOrders = docs.filter(
              (o) =>
                !!(o as Order & { externalDeliveryId?: string }).externalDeliveryId,
            );
            applySlice(sourceValue, courierOrders);
            setIsLoading(false);
          },
          (listenerErr) => {
            if (!mounted) return;
            // First-party listener error is non-fatal; the third-party
            // subscription (the primary live signal) may still be live.
            setError((prev) => prev ?? listenerErr);
          },
        );
      };

      const unsubPos = subscribeFirstPartyCourier("pos");
      const unsubOnline = subscribeFirstPartyCourier("online");

      unsubscribeRef.current = () => {
        unsubThirdParty();
        unsubPos();
        unsubOnline();
      };
    };

    void attachListener();

    return () => {
      mounted = false;
      stopPollFallback();
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [tenantId, locationNumericId]);

  // Manual refresh affordance — runs the same fetch the poll fallback uses,
  // once. Lets the board offer a "Refresh" button during the poll-fallback
  // window (when the live listener is down and updates are 30s apart).
  const refresh = useCallback(() => {
    void runPoll();
  }, [runPoll]);

  return { orders, isLoading, error, isLive, lastUpdatedAt, refresh };
};

// ─────────────────────────────────────────────────────────────────────────
// Real-time subscription: EVERY order for the restaurant
//
// Distinct from `useActiveOrdersSubscription` above, which is the Active
// Deliveries board's feed and deliberately keeps only courier-bearing orders
// (it filters on `externalDeliveryId`). That filter is why the Live Orders page
// could not reuse it: a dine-in or takeout order has no courier leg, so the
// board's subscription never sees it.
//
// Why this exists at all: the Live Orders page ran on `useGetRestaurantOrdersPage`
// — a one-shot `useQuery` with a 2-minute `staleTime`. So a page titled "Live
// Orders" could be two minutes behind, and nothing re-rendered when an order
// changed state. Measured against the POS on 2026-09-12: a paid DoorDash order
// sitting in `Ready` was not visible in management until it was marked served.
//
// Shape notes:
//   - No `orderSource` condition, so one listener covers every channel. The
//     unfiltered (createdAt desc) ordering is served by the single-field index,
//     so this needs NO composite index — the same query
//     `getRestaurantOrdersPage` already issues for its unfiltered case.
//   - Over-fetches by one to OBSERVE truncation rather than infer it, matching
//     `getRestaurantOrdersPage`. The page header claims "All orders from every
//     source", which stops being true at the cap, so `hasMore` is surfaced.
//   - Falls back to a 30s poll if the listener errors (offline, quota), and
//     reports `isLive` so the UI can say which mode it is in. Silence about a
//     dead listener is indistinguishable from a quiet restaurant.
// ─────────────────────────────────────────────────────────────────────────

const LOCATION_ORDERS_SUBSCRIPTION_LIMIT = 100;

/** Query-key used to store the live all-orders list in the TanStack cache. */
function locationOrdersSubscriptionKey(
  tenantId: number,
  locationNumericId: number,
  limit: number,
) {
  return [
    ...orderKeys.lists(),
    "restaurant",
    "subscription",
    { tenantId, restaurantId: locationNumericId, limit },
  ] as const;
}

/**
 * Subscribe to every order for a restaurant in real time via Firestore
 * onSnapshot.
 *
 * Returns `{orders, hasMore, isLoading, error, isLive, lastUpdatedAt, refresh}`
 * — `hasMore` true means the newest `limit` are shown and older ones are not,
 * so do not present the list as complete.
 */
const useLocationOrdersSubscription = (
  tenantId: number,
  locationNumericId: number,
  limit: number = LOCATION_ORDERS_SUBSCRIPTION_LIMIT,
) => {
  const queryClient = useQueryClient();
  const cacheKey = locationOrdersSubscriptionKey(tenantId, locationNumericId, limit);

  const [orders, setOrders] = useState<Order[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isLive, setIsLive] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stable poll callback — the fallback when the listener errors, and the
  // `refresh()` affordance. Same code path as the one-shot page query it
  // replaces, so the fallback is a known quantity.
  const runPoll = useCallback(async () => {
    if (!tenantId || !locationNumericId) return;
    try {
      const page = await orderService.getRestaurantOrdersPage(
        tenantId,
        locationNumericId,
        undefined,
        limit,
      );
      setOrders(page.orders);
      setHasMore(page.hasMore);
      queryClient.setQueryData(cacheKey, page.orders);
      setLastUpdatedAt(new Date());
      setError(null);
      setIsLoading(false);
    } catch (pollErr) {
      setError(pollErr instanceof Error ? pollErr : new Error(String(pollErr)));
      setIsLoading(false);
    }
    // cacheKey is derived from the three primitives below, so it is stable
    // across renders that do not change them.
  }, [tenantId, locationNumericId, limit, queryClient]);

  useEffect(() => {
    if (!tenantId || !locationNumericId) return;

    let mounted = true;

    const startPollFallback = () => {
      if (pollTimerRef.current) return;
      setIsLive(false);
      void runPoll();
      pollTimerRef.current = setInterval(
        () => void runPoll(),
        POLL_FALLBACK_INTERVAL_MS,
      );
    };

    const stopPollFallback = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const attachListener = async () => {
      let tenantDocId: string;
      let locationDocId: string;
      try {
        // Shared session-cached resolver — the same one
        // getRestaurantOrdersPage uses, so mounting this hook beside those
        // queries costs no extra tenant/restaurant reads.
        const resolved = await resolveDocIds(tenantId, locationNumericId);
        tenantDocId = resolved.tenantDocId;
        locationDocId = resolved.restaurantDocId;
      } catch (resolveErr) {
        if (!mounted) return;
        setError(
          resolveErr instanceof Error ? resolveErr : new Error(String(resolveErr)),
        );
        startPollFallback();
        return;
      }

      const repo = new FirestoreRepository<Order>(
        CollectionPaths.orders(tenantDocId, locationDocId),
        Order,
      );

      unsubscribeRef.current = repo.subscribeToCollection(
        [],
        [{ field: "createdAt", direction: "desc" }],
        (docs) => {
          if (!mounted) return;
          const truncated = docs.length > limit;
          // Checkout-held orders (online, not yet paid) are on no staff board.
          const page = (truncated ? docs.slice(0, limit) : docs).filter(
            (o) => !isCheckoutHeldOrderState((o as Order).orderState),
          );
          setOrders(page as Order[]);
          setHasMore(truncated);
          queryClient.setQueryData(cacheKey, page);
          setLastUpdatedAt(new Date());
          setIsLive(true);
          setIsLoading(false);
          setError(null);
          stopPollFallback();
        },
        (listenerErr) => {
          if (!mounted) return;
          setIsLive(false);
          setError(listenerErr);
          startPollFallback();
        },
        // Over-fetch by one so truncation is observed, not inferred.
        limit + 1,
      );
    };

    void attachListener();

    return () => {
      mounted = false;
      stopPollFallback();
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
    // Deps are the three identity primitives only — matching
    // useActiveOrdersSubscription above. runPoll is stable in the same inputs,
    // and adding it would re-attach the listener on every render.
  }, [tenantId, locationNumericId, limit]);

  const refresh = useCallback(() => {
    void runPoll();
  }, [runPoll]);

  return { orders, hasMore, isLoading, error, isLive, lastUpdatedAt, refresh };
};

export {
  orderKeys,
  useActiveOrdersSubscription,
  useGetCustomerOrders,
  useGetExternalOrders,
  useGetOrderAnalytics,
  useGetOrderByCartId,
  useGetOrderByGuid,
  useGetOrderById,
  useGetPlatformOrders,
  // Management & filtering hooks
  useGetLocationOrders,
  useGetLocationOrdersPage,
  useGetThirdPartyOrders,
  useLocationOrdersSubscription,
  useUpdateOrderStatus,
};
